import { istGeraet } from './gleis';
import { QrScanner } from './qrScanner';

/**
 * QR-Codes scannen — Prüflingsausweise, Einrichtungscodes und Auswahlcodes.
 *
 * ⚠️ **Seit dem 2026-09-14 über die eigene Bühne** (`QrScannerViewController.swift`), nicht mehr
 * über `@capacitor/barcode-scanner`. Zwei Vorgaben des Nutzers gingen mit dem Fremdplugin nicht:
 * ein **quadratisches** Sichtfenster (dort ist die Form fest verdrahtet und dreht auf dem iPad
 * mit) und ein **Selbstschluss nach drei Minuten** (dort gibt es kein `stopScan`, das Fenster
 * kommt erst bei Fund oder Tipp zurück). Einzelheiten in `platform/qrScanner.ts`.
 *
 * Der Eigenbau `myscanner` der alten App wird damit nicht nachgebaut: die Bühne ist unsere,
 * die Erkennung macht AVFoundation.
 */

/**
 * Ob überhaupt gescannt werden kann.
 *
 * ⚠️ **Seit dem 2026-09-22 auch im Browser wahr**, wenn er `BarcodeDetector` und eine Kamera
 * hat (`platform/qrScannerWeb.ts`). Vorher stand hier eine Frage nach der Plattform — das war
 * keine Fähigkeitsprüfung, sondern eine Annahme über die Plattform.
 *
 * ⚠️ **Die Formatliste wird hier nicht abgefragt.** `getSupportedFormats()` ist eine Zusage,
 * diese Funktion dagegen wird **im Rendern** gebraucht (`ScannerScreen.tsx`) und muss ohne
 * Warten antworten. Fehlt `qr_code` wider Erwarten, fällt es beim Öffnen auf — `scan()` gibt
 * dann „abgebrochen", und das ist derselbe harmlose Ausgang wie ein Tipp auf Abbrechen.
 */
export const canScan = () =>
  istGeraet() ||
  (typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof (globalThis as { BarcodeDetector?: unknown }).BarcodeDetector !== 'undefined');

/**
 * Die Frist, nach der sich das Fenster von selbst schliesst — Vorgabe des Nutzers (2026-09-14).
 *
 * Ein iPad, das mit offener Kamera auf dem Tisch liegenbleibt, kommt so von selbst zurück in
 * die App. Die Bühne des Prüflingsausweises gibt ihre **eigene** Frist mit, damit die Kamera
 * nicht länger offen steht als die Uhr darunter läuft (`ScannerScreen.tsx`).
 */
export const SCAN_FRIST_SEKUNDEN = 180;

/**
 * Öffnet den Scanner und gibt den gelesenen Text zurück.
 *
 * `null` heisst: **kein Code**. Drei Wege führen dahin — Abbruch, abgelaufene Frist, und im
 * Browser das Fehlen der Bühne. Keiner davon ist ein Fehler, und keiner soll eine Meldung
 * zeigen: wer die Kamera öffnet und es sich anders überlegt, hat nichts falsch gemacht.
 * Geworfen wird nur, wenn wirklich etwas schiefging (keine Kamera, keine Erlaubnis).
 */
export async function scanCandidateCode(
  anleitung = 'QR-Code des Prüflings in den Rahmen halten',
  fristSekunden = SCAN_FRIST_SEKUNDEN,
): Promise<string | null> {
  const ergebnis = await QrScanner.scan({
    instructions: anleitung,
    timeoutSeconds: fristSekunden,
  });

  const text = ergebnis.value?.trim();
  return text ? text : null;
}
